import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { RotateCw, Clock, Database } from 'lucide-react';
import { Table, Select, Empty } from 'antd';
import axios from 'axios';

const { Option } = Select;

const TopSQLQueries = ({ databaseInfo }) => {
  const { projectId } = useParams();
  const [topSQLQueries, setTopSQLQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timeRange, setTimeRange] = useState(3600); // Default 1 hour in seconds
  const [maxQueries, setMaxQueries] = useState(10); // Default 10 queries

  const databaseIdentifier = databaseInfo?.databaseName;

  const fetchTopSQLQueries = async () => {
    if (!databaseIdentifier) return;
    
    try {
      setLoading(true);
      const response = await axios.get(
        `https://cloudify-prod.wizr.in/rds/instances/${databaseIdentifier}/performance-insights/top-sql`, 
        {
          params: {
            timeRange,
            maxQueries
          }
        }
      );
      
      setTopSQLQueries(response.data.top_sql_queries || []);
    } catch (error) {
      console.error('Failed to fetch top SQL queries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (databaseIdentifier) {
      fetchTopSQLQueries();
    }
  }, [databaseIdentifier, timeRange, maxQueries]);

  const columns = [
    {
      title: 'SQL Query',
      dataIndex: 'sql_text',
      key: 'sql_text',
      width: '75%',
      render: (text) => (
        <div className="max-h-20 overflow-y-auto">
          <pre className="text-xs text-gray-700 whitespace-pre-wrap">{text}</pre>
        </div>
      ),
    },
    {
      title: 'Avg Load',
      dataIndex: 'avg_load',
      key: 'avg_load',
      width: '25%',
      render: (load) => (
        <div className="flex items-center">
          <div className="w-full bg-gray-200 rounded-full h-4 mr-2">
            <div 
              className="bg-indigo-600 h-4 rounded-full" 
              style={{ width: `${Math.min(load * 10, 100)}%` }}
            />
          </div>
          <span className="text-sm font-medium">{load.toFixed(2)}</span>
        </div>
      ),
      sorter: (a, b) => a.avg_load - b.avg_load,
      defaultSortOrder: 'descend',
    },
  ];

  const timeRangeOptions = [
    { label: 'Last 15 Minutes', value: 900 },
    { label: 'Last 30 Minutes', value: 1800 },
    { label: 'Last 1 Hour', value: 3600 },
    { label: 'Last 3 Hours', value: 10800 },
    { label: 'Last 6 Hours', value: 21600 },
    { label: 'Last 12 Hours', value: 43200 },
    { label: 'Last 24 Hours', value: 86400 },
  ];

  const queryCountOptions = [
    { label: '5 Queries', value: 5 },
    { label: '10 Queries', value: 10 },
    { label: '20 Queries', value: 20 },
    { label: '50 Queries', value: 50 },
  ];

  return (
    <div className="space-y-6 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-center pb-4 border-b border-gray-100">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">Top SQL Queries</h2>
          <p className="text-gray-500 text-sm">View database load by SQL query</p>
        </div>
        
        <div className="flex space-x-4 items-center">
          <Select
            value={timeRange}
            onChange={setTimeRange}
            style={{ width: 160 }}
            className="text-sm"
          >
            {timeRangeOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
          
          <Select
            value={maxQueries}
            onChange={setMaxQueries}
            style={{ width: 120 }}
            className="text-sm"
          >
            {queryCountOptions.map(option => (
              <Option key={option.value} value={option.value}>{option.label}</Option>
            ))}
          </Select>
          
          <button
            onClick={fetchTopSQLQueries}
            disabled={loading}
            className={`
              p-2 rounded-full transition-all duration-200 relative
              ${loading ? 'bg-gray-50 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'}
            `}
          >
            <RotateCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      
      <div className="rounded-lg overflow-hidden border border-gray-200">
        {topSQLQueries.length > 0 ? (
          <Table
            columns={columns}
            dataSource={topSQLQueries}
            rowKey={(record, index) => index}
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} queries`,
            }}
          />
        ) : (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span className="text-gray-500">
                No SQL queries data available. Performance Insights may not be enabled for this database.
              </span>
            }
          />
        )}
      </div>
      
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-sm text-gray-600">
        <div className="flex items-start">
          <Database className="h-5 w-5 mr-2 text-indigo-600 mt-0.5" />
          <div>
            <p className="font-medium">About Performance Insights</p>
            <p className="mt-1">
              This feature requires Performance Insights to be enabled on your RDS instance. 
              The data shows which SQL queries are consuming the most database resources, 
              helping you identify and optimize problematic queries.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopSQLQueries;